// Loan Compliance Module v1.0 PR 3 — list of Legal Ops cadence items
// (next 30 days + overdue) sourced from ComplianceCalendarItem rows whose
// sourceType is one of the Legal Ops cadence sources.

import {
  Badge,
  Card,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from "@/design-system/components/ui";
import type { ComplianceCalendarItemDto } from "@/api/portfolioAdmin/types";
import { useLoanDueDates } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import type { LegalOpsModule } from "@/api/compliance/types";
import {
  bandForDueDate,
  daysLabel,
  daysUntil,
  calendarDate,
} from "../common/colorBand";

interface UpcomingOverdueSectionProps {
  loanId: string;
  onPrefillRecordCheck: (args: {
    module: LegalOpsModule;
    calendarItemId: string;
    periodLabel: string;
  }) => void;
}

// Source-type → module mapping comes straight from the PR 2 server map:
// PhoenixCapital.Infrastructure.Services.Compliance.LegalOpsCheckService.ModuleToSourceTypes.
const SOURCE_TYPE_TO_MODULE: Record<string, LegalOpsModule> = {
  "legalops-identity-annual": "LoanIdentity",
  "ucc-continuation-window-open": "UccFilingMonitoring",
  "ucc-continuation-warning-90": "UccFilingMonitoring",
  "ucc-continuation-warning-30": "UccFilingMonitoring",
  "ucc-5yr-lapse": "UccFilingMonitoring",
  "legalops-ucc-per-segment-contribution": "UccFilingMonitoring",
  "insurance-renewal": "InsuranceRenewal",
  "insurance-expiration-warning": "InsuranceRenewal",
  "legalops-daca-monthly": "DacaMonitoring",
  "daca-monthly-review": "DacaMonitoring",
};

export const LEGAL_OPS_SOURCE_TYPES = new Set(Object.keys(SOURCE_TYPE_TO_MODULE));

function isLegalOpsItem(item: ComplianceCalendarItemDto): boolean {
  return LEGAL_OPS_SOURCE_TYPES.has(item.sourceType);
}

function isOpenStatus(status: string): boolean {
  const s = status.toLowerCase();
  return s !== "completed" && s !== "waived";
}

function moduleFor(item: ComplianceCalendarItemDto): LegalOpsModule {
  return SOURCE_TYPE_TO_MODULE[item.sourceType] ?? "LoanIdentity";
}

export function UpcomingOverdueSection({
  loanId,
  onPrefillRecordCheck,
}: UpcomingOverdueSectionProps) {
  const { data, isLoading } = useLoanDueDates(loanId);
  const all = (data ?? []).filter(
    (i) => isLegalOpsItem(i) && isOpenStatus(i.status),
  );
  const filtered = all.filter((i) => {
    const days = daysUntil(i.dueDate);
    return days !== null && days <= 30;
  });
  filtered.sort(
    (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
  );

  return (
    <Card>
      <div className="mb-space-3">
        <h3 className="font-display font-bold text-h3 m-0">
          Upcoming &amp; overdue
        </h3>
        <p className="text-caption text-gray-mid m-0 mt-space-1">
          Legal Ops cadence items due within 30 days or already overdue.
        </p>
      </div>

      {isLoading ? (
        <p className="text-small text-gray-mid italic m-0">Loading…</p>
      ) : filtered.length === 0 ? (
        <p
          className="text-small text-gray-mid m-0"
          data-testid="legal-ops-upcoming-empty"
        >
          Nothing due within 30 days.
        </p>
      ) : (
        <Table data-testid="legal-ops-upcoming-table">
          <THead>
            <TR>
              <TH>Module</TH>
              <TH>Item</TH>
              <TH>Due</TH>
              <TH>Status</TH>
            </TR>
          </THead>
          <TBody>
            {filtered.map((i) => {
              const module = moduleFor(i);
              // Same countdown, twice: the label and the row's band. One `tz`.
              const days = daysUntil(i.dueDate);
              const band = bandForDueDate(i.dueDate);
              return (
                <TR
                  key={i.id}
                  className="cursor-pointer hover:bg-white-warm"
                  data-testid={`legal-ops-upcoming-row-${i.id}`}
                  onClick={() =>
                    onPrefillRecordCheck({
                      module,
                      calendarItemId: i.id,
                      periodLabel: i.title,
                    })
                  }
                >
                  <TD>{module}</TD>
                  <TD>
                    <div className="font-semibold">{i.title}</div>
                    <div className="text-caption text-gray-mid font-mono">
                      {i.sourceType}
                    </div>
                  </TD>
                  <TD>
                    {calendarDate(i.dueDate)}
                    <div className="text-caption text-gray-mid">
                      {daysLabel(days)}
                    </div>
                  </TD>
                  <TD>
                    <Badge
                      status={
                        band === "red"
                          ? "error"
                          : band === "orange" || band === "yellow"
                            ? "review"
                            : "finalized"
                      }
                    >
                      {i.status}
                    </Badge>
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      )}
    </Card>
  );
}
